import { useMemo, useState, useCallback } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { chain, filter, includes } from "lodash"
import { IconButton, Menu } from "@mui/material"
import { MoreVert } from "@mui/icons-material"
import { useQueryStringState } from "hooks/query-string"
import { useSaveContextPreferences } from "hooks/preferences"
import { LONG, LONG_SHORT } from "resources/position-sets/composition"
import CompareSearch from "modules/comparison/components/compare-search"
import { Creators as DialogCreators } from "modules/dialog/model"
import { useAccountAlias } from "resources/account"
import { useModelMetadata } from "resources/models"
import OPTooltip from "components/material/tooltip"
import { BASKET, BENCHMARK, CONTRIBUTION, ETF, INDEX, PORTFOLIO, POSITION_SET, STANDALONE } from "../position-sets"
import ItemHeaderMenuItem from "./item-header-menu-item"

const EMPTY_ARRAY = []

function generateBaseSelectionActions(addBase) {
  return {
    benchmark: {
      mainAction: item => () => { addBase({ type: BENCHMARK, id: item.id }) },
      noDispatch: true,
    },
    index: {
      mainAction: item => () => { addBase({ type: INDEX, id: item.id }) },
      noDispatch: true,
    },
    security: {
      mainAction: item => () => {
        let newPositionSet
        switch (true) {
          case item.assetClass === "swap":
            newPositionSet = { type: BASKET, id: item.ticker }
            break
          case item.assetSubclass === "etf":
            newPositionSet = { type: ETF, id: item.internalId }
            break
        }

        addBase(newPositionSet)
      },
      noDispatch: true,
    },
    portfolio: {
      mainAction: item => () => { addBase({ type: PORTFOLIO, id: item.alias }) },
      noDispatch: true,
    },
  }
}

export default function CompareItemHeader({ modelId, positionSet, index, remainingColumns, isContextActive }) {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()
  const [modelMetadata] = useModelMetadata(modelId)
  const [anchorEl, setAnchorEl] = useState(null)
  const [{ positionSets: queryStringPositionSets = EMPTY_ARRAY }, updateQueryString] = useQueryStringState()
  const savePositionSetsPreference = useSaveContextPreferences("positionSets")

  const modelSupportsComposites = useMemo(
    () => !includes(modelMetadata?.missingDataFiles, "composite"),
    [modelMetadata],
  )

  const disableSearchResult = useCallback((asset) => {
    if (asset.assetSubclass === "stock") {
      return true
    }
    if (!modelSupportsComposites && asset.assetSubclass === "etf") {
      return true
    }
    return false
  }, [modelSupportsComposites])

  const currentIndex = index - 1

  const menuOptions = useMemo(() => {
    const handleRemove = () => {
      setAnchorEl(false)
      const filteredPositionSet = filter(queryStringPositionSets, (_, i) => i !== currentIndex)
      updateQueryString({ positionSets: filteredPositionSet })
      savePositionSetsPreference(filteredPositionSet)
    }

    const handleSplitLongShort = () => {
      setAnchorEl(false)
      const newPositionSets = [
        ...queryStringPositionSets,
        {
          ...positionSet.queryStringPositionSet,
          segment: {
            filters: { longShort: "LONG" },
            normalization: CONTRIBUTION,
          },
        }, {
          ...positionSet.queryStringPositionSet,
          segment: {
            filters: { longShort: "SHORT" },
            normalization: CONTRIBUTION,
          },
        },
      ]
      updateQueryString({ positionSets: newPositionSets })
      savePositionSetsPreference(newPositionSets)
    }

    const updateCurrentPositionSet = (updatedPositionSet) => {
      setAnchorEl(false)
      const newPositionSets = [
        ...queryStringPositionSets.slice(0, currentIndex),
        updatedPositionSet,
        ...queryStringPositionSets.slice(currentIndex + 1),
      ]
      updateQueryString({ positionSets: newPositionSets })
      savePositionSetsPreference(newPositionSets)
    }

    const handleEvaluateAs = () => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        asShort: positionSet.evaluatedAsShort ? undefined : true,
      })
    }

    const handleDisableBase = () => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        base: false,
      })
    }

    const handleUseDefaultBase = () => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        base: undefined,
      })
    }

    const handleSetCustomBase = (base) => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        base,
      })
    }

    const handleNormalizeAs = () => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        segment: {
          ...queryStringPositionSets[currentIndex].segment,
          normalization: queryStringPositionSets[currentIndex].segment.normalization === CONTRIBUTION ? STANDALONE : CONTRIBUTION,
        },
      })
    }

    const handleShowBaseSelector = () => {
      setAnchorEl(false)

      dispatch(DialogCreators.open({
        title: "Search For Base",
        content: <CompareSearch account={accountAlias} modelId={modelId} disableSearchResult={disableSearchResult} itemsActions={generateBaseSelectionActions(handleSetCustomBase)} />,
      }))
    }

    const handleToggleFxHedge = (enabled = false) => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        fxHedgeEnabled: enabled,
      })
    }

    let actions = [{
      condition: !positionSet.fixed,
      action: {
        label: "Remove",
        fn: handleRemove,
      },
    }]

    if (!positionSet.unavailable) {
      actions = actions.concat([{
        condition: !positionSet.fixed
          && ![PORTFOLIO, BENCHMARK].includes(positionSet.type)
          && positionSet.longShort === LONG,
        action: {
          label: `Evaluate As ${positionSet.evaluatedAsShort ? "Long" : "Short"}`,
          fn: handleEvaluateAs,
        },
      }, {
        condition: positionSet.longShort === LONG_SHORT,
        action: {
          label: "Split into LONG/SHORT",
          fn: handleSplitLongShort,
          disabled: remainingColumns < 2,
          disabledTooltip: "There is not enough space to add the 2 segments",
        },
      }, {
        condition: positionSet.normalization,
        action: {
          label: `Normalize as ${positionSet.normalization === CONTRIBUTION ? "Standalone" : "Contributor"}`,
          fn: handleNormalizeAs,
        },
      }, {
        isSpacer: true,
      }, {
        condition: !positionSet.fixed,
        action: {
          label: "Select Custom Base",
          fn: handleShowBaseSelector,
        },
      }, {
        condition: !positionSet.fixed && isContextActive && positionSet.queryStringPositionSet.base !== false,
        action: {
          label: "Disable Base",
          fn: handleDisableBase,
        },
      }, {
        condition: !positionSet.fixed && positionSet.queryStringPositionSet.base !== undefined,
        action: {
          label: "Remove Custom Base",
          fn: handleUseDefaultBase,
        },
      }, {
        condition: !positionSet.fixed && positionSet.fxHedgeEnabled,
        action: {
          label: "Disable FX Hedge",
          fn: () => handleToggleFxHedge(false),
        },
      }, {
        condition: !positionSet.fixed && !positionSet.fxHedgeEnabled,
        action: {
          label: "Enable FX Hedge",
          fn: () => handleToggleFxHedge(true),
          disabled: !positionSet.isFxHedgeSupported,
          disabledTooltip: "FX Hedge is not supported by the current model",
        },
      }])
    }

    return chain(actions)
      .filter(({ condition, isSpacer }) => condition || isSpacer)
      .map(({ action, isSpacer }) => action || isSpacer)
      .value()
  }, [positionSet, currentIndex, queryStringPositionSets, savePositionSetsPreference, updateQueryString, remainingColumns, isContextActive, dispatch, accountAlias, modelId, disableSearchResult])

  const menu = chain(menuOptions)
    .map((menuOption, index) => {
      if (menuOption === true) {
        if (index === 0 || index === menuOptions.length - 1) { // don't display spacers at start and end
          return null
        }
        return <hr key={index} />
      }
      return <ItemHeaderMenuItem key={index} {...menuOption} />
    })
    .compact()
    .value()

  const title = positionSet.unavailable ? "Not Found" : positionSet.name

  return <div data-test-class="comparison-item" className="comparison-item-wrapper">
    <OPTooltip title={title}>
      <span className="comparison-item-label">{title}</span>
    </OPTooltip>
    {menu.length > 0 && <>
      <IconButton size="small" onClick={event => { setAnchorEl(event.target) }} data-test-class="comparison-item-menu-button"><MoreVert /></IconButton>
      <Menu
        data-test-class="comparison-item-menu-popover"
        disablePortal
        transformOrigin={{ horizontal: "right" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(false)}
      >
        {menu}
      </Menu>
    </>}
  </div>
}

CompareItemHeader.propTypes = {
  modelId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  positionSet: POSITION_SET.isRequired,
  remainingColumns: PropTypes.number.isRequired,
  isContextActive: PropTypes.bool.isRequired,
}
